import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, FileText, Send, Eye, Trash2, Check, Clock, Search, Filter, Download, X, ExternalLink, Archive, FolderArchive, Settings, Upload, FileSignature } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { settingsApi, mediaApi } from '@/lib/api';
import { useAdminLanguage } from '@/context/AdminLanguageContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminContracts() {
  const { t } = useAdminLanguage();
  const [searchParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [sending, setSending] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archiveId, setArchiveId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [activeTab, setActiveTab] = useState('contracts');
  
  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    car_id: '',
    price: '',
    payment_method: 'Bar',
    deposit: '',
    warranty_months: 12,
    delivery_date: '',
    additional_terms: '',
    additional_terms_en: ''
  });

  useEffect(() => {
    loadData();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSettingsChange = async (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    // Auto-save
    setSaving(true);
    try {
      await settingsApi.update({ [field]: value });
    } catch (error) {
      toast.error(t('msg_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const response = await mediaApi.upload(file);
      const url = response.data.url;
      handleSettingsChange('dealer_signature_url', url);
      toast.success(t('contracts_signature_uploaded') || 'Signature uploaded');
    } catch (error) {
      toast.error(t('contracts_upload_error') || 'Upload error');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  // Check for customer param and open form
  useEffect(() => {
    const customerId = searchParams.get('customer');
    if (customerId && customers.length > 0) {
      handleCustomerSelect(customerId);
      setShowForm(true);
    }
  }, [searchParams, customers]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [contractsRes, customersRes, carsRes] = await Promise.all([
        axios.get(`${API_URL}/api/contracts`, { headers }),
        axios.get(`${API_URL}/api/customers`, { headers }),
        axios.get(`${API_URL}/api/cars`)
      ]);

      setContracts(contractsRes.data);
      setCustomers(customersRes.data);
      setCars(carsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('msg_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveId) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API_URL}/api/contracts/${archiveId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('contracts_archived_success') || 'Contract archived');
      loadData();
    } catch (error) {
      toast.error(t('contracts_archive_error') || 'Archive error');
    } finally {
      setArchiveId(null);
    }
  };

  // Calculate stats
  const signedCount = contracts.filter(c => c.status === 'signed' && !c.archived).length;
  const sentCount = contracts.filter(c => c.status === 'sent' && !c.archived).length;
  const openCount = contracts.filter(c => (c.status === 'draft' || c.status === 'pending') && !c.archived).length;
  const archivedCount = contracts.filter(c => c.archived).length;

  // When customer is selected, pre-fill from their data
  const handleCustomerSelect = (customerId) => {
    setFormData(prev => ({ ...prev, customer_id: customerId }));
    
    // If customer has an interested car, select it
    const customer = customers.find(c => c.id === customerId);
    if (customer?.interested_car_id) {
      const car = cars.find(c => c.id === customer.interested_car_id);
      if (car) {
        setFormData(prev => ({
          ...prev,
          car_id: customer.interested_car_id,
          price: car.price.toString()
        }));
      }
    }
  };

  // When car is selected, update price
  const handleCarSelect = (carId) => {
    const car = cars.find(c => c.id === carId);
    if (car) {
      setFormData(prev => ({
        ...prev,
        car_id: carId,
        price: car.price.toString()
      }));
    }
  };

  const handleSubmit = async (e, sendImmediately = false) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    // Get customer and car details
    const customer = customers.find(c => c.id === formData.customer_id);
    const car = cars.find(c => c.id === formData.car_id);

    if (!customer || !car) {
      toast.error(t('contracts_select_customer_car') || 'Please select customer and vehicle');
      return;
    }

    const contractData = {
      customer_id: formData.customer_id,
      car_id: formData.car_id,
      buyer_name: `${customer.first_name} ${customer.last_name}`,
      buyer_email: customer.email,
      buyer_address: customer.address || '',
      buyer_phone: customer.phone || '',
      car_brand: car.brand,
      car_model: car.model,
      car_year: car.year,
      car_vin: car.internal_id || '',
      car_mileage: car.mileage || null,
      car_color: car.color || '',
      price: parseFloat(formData.price),
      deposit: formData.deposit ? parseFloat(formData.deposit) : null,
      payment_method: formData.payment_method,
      warranty_months: parseInt(formData.warranty_months),
      delivery_date: formData.delivery_date || null,
      additional_terms: formData.additional_terms,
      additional_terms_en: formData.additional_terms_en
    };

    try {
      if (editingContract) {
        await axios.put(`${API_URL}/api/contracts/${editingContract.id}`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('contracts_updated') || 'Contract updated');
        loadData();
        resetForm();
      } else {
        const response = await axios.post(`${API_URL}/api/contracts`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newContractId = response.data.id;
        
        if (sendImmediately) {
          // Create and send immediately
          toast.success(t('contracts_created') || 'Contract created');
          await handleSendContract(newContractId);
        } else {
          // Just create as draft
          toast.success(t('contracts_saved_draft') || 'Contract saved as draft');
        }
        
        loadData();
        resetForm();
      }
    } catch (error) {
      toast.error(t('msg_error'));
    }
  };

  const handleSendContract = async (contractId) => {
    setSending(contractId);
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API_URL}/api/contracts/${contractId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('contracts_sent_email') || 'Contract sent via email');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('contracts_send_error') || 'Send error');
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm(t('contracts_delete_confirm') || 'Really delete contract?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('contracts_deleted') || 'Contract deleted');
      loadData();
    } catch (error) {
      toast.error(t('contracts_delete_error') || 'Delete error');
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      customer_id: contract.customer_id || '',
      car_id: contract.car_id || '',
      price: contract.price?.toString() || '',
      payment_method: contract.payment_method || 'Bar',
      deposit: contract.deposit?.toString() || '',
      warranty_months: contract.warranty_months || 12,
      delivery_date: contract.delivery_date || '',
      additional_terms: contract.additional_terms || '',
      additional_terms_en: contract.additional_terms_en || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingContract(null);
    setFormData({
      customer_id: '',
      car_id: '',
      price: '',
      payment_method: 'Bar',
      deposit: '',
      warranty_months: 12,
      delivery_date: '',
      additional_terms: '',
      additional_terms_en: ''
    });
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'draft': 'bg-gray-100 text-gray-700',
      'pending': 'bg-amber-100 text-amber-700',
      'sent': 'bg-blue-100 text-blue-700',
      'signed': 'bg-green-100 text-green-700',
      'cancelled': 'bg-red-100 text-red-700'
    };
    const labels = {
      'draft': t('contracts_status_draft'),
      'pending': t('contracts_status_pending'),
      'sent': t('contracts_status_sent'),
      'signed': t('contracts_status_signed'),
      'cancelled': t('contracts_status_cancelled')
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Filter contracts
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = searchTerm === '' || 
      contract.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.car_brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.car_model?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    
    // Filter by archived status
    const matchesArchived = showArchived ? contract.archived : !contract.archived;
    
    return matchesSearch && matchesStatus && matchesArchived;
  });

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="admin-contracts">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl">{t('contracts_title')}</h1>
          <p className="text-[#666666]">{contracts.filter(c => !c.archived).length} {t('contracts_active')}</p>
        </div>
        {activeTab === 'contracts' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors text-sm ${
                showArchived 
                  ? 'border-amber-500 bg-amber-50 text-amber-700' 
                  : 'border-[#E5E5E5] hover:bg-[#F9F9F7]'
              }`}
            >
              <Archive size={16} />
              {showArchived ? t('contracts_show_active') : `${t('contracts_archive')} (${archivedCount})`}
            </button>
            <Button
              onClick={() => setShowForm(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="new-contract-btn"
            >
              <Plus size={18} />
              {t('contracts_new')}
            </Button>
          </div>
        )}
      </div>

      {/* Hidden file input for signature upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleSignatureUpload}
        accept="image/*"
        className="hidden"
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-[#F9F9F7] p-1 rounded-lg">
          <TabsTrigger value="contracts" className="flex items-center gap-2 data-[state=active]:bg-white">
            <FileText size={16} />
            {t('contracts_title')}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Settings size={16} />
            {t('contracts_settings')}
          </TabsTrigger>
        </TabsList>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="mt-6">
          {/* Stats */}
          {!showArchived && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-[#E5E5E5] p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-green-600">{signedCount}</p>
                <p className="text-sm text-[#666666]">{t('contracts_status_signed')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5E5E5] p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-blue-600">{sentCount}</p>
                <p className="text-sm text-[#666666]">{t('contracts_status_sent')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5E5E5] p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-amber-600">{openCount}</p>
                <p className="text-sm text-[#666666]">{t('contracts_open')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl">
                {editingContract ? t('contracts_edit') : t('contracts_new_purchase')}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-[#F9F9F7] rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer & Car Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">{t('contracts_customer')} *</label>
                  <Select value={formData.customer_id} onValueChange={handleCustomerSelect}>
                    <SelectTrigger data-testid="contract-customer">
                      <SelectValue placeholder={t('contracts_select_customer')} />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(customer => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.first_name} {customer.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('contracts_vehicle')} *</label>
                  <Select value={formData.car_id} onValueChange={handleCarSelect}>
                    <SelectTrigger data-testid="contract-car">
                      <SelectValue placeholder={t('contracts_select_vehicle')} />
                    </SelectTrigger>
                    <SelectContent>
                      {cars.filter(c => !c.is_sold).map(car => (
                        <SelectItem key={car.id} value={car.id}>
                          {car.brand} {car.model} ({car.year}) - {formatPrice(car.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Price & Payment */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="overline block mb-2">{t('contracts_price')} (€) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="input-premium w-full"
                    required
                    data-testid="contract-price"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">{t('contracts_deposit')} (€)</label>
                  <input
                    type="number"
                    value={formData.deposit}
                    onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                    className="input-premium w-full"
                    data-testid="contract-deposit"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">{t('contracts_payment_method')}</label>
                  <Select 
                    value={formData.payment_method} 
                    onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
                  >
                    <SelectTrigger data-testid="contract-payment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bar">{t('contracts_title') === 'Contracts' ? 'Cash Payment' : 'Barzahlung'}</SelectItem>
                      <SelectItem value="Überweisung">{t('contracts_title') === 'Contracts' ? 'Bank Transfer' : 'Überweisung'}</SelectItem>
                      <SelectItem value="Finanzierung">{t('contracts_title') === 'Contracts' ? 'Financing' : 'Finanzierung'}</SelectItem>
                      <SelectItem value="Leasing">{t('contracts_title') === 'Contracts' ? 'Leasing' : 'Leasing'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Warranty & Delivery */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">{t('contracts_warranty')} ({t('contracts_months')})</label>
                  <Select 
                    value={formData.warranty_months.toString()} 
                    onValueChange={(v) => setFormData({ ...formData, warranty_months: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="contract-warranty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">{t('contracts_title') === 'Contracts' ? 'No Warranty' : 'Keine Garantie'}</SelectItem>
                      <SelectItem value="6">6 {t('contracts_months')}</SelectItem>
                      <SelectItem value="12">12 {t('contracts_months')}</SelectItem>
                      <SelectItem value="24">24 {t('contracts_months')}</SelectItem>
                      <SelectItem value="36">36 {t('contracts_months')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline block mb-2">{t('contracts_delivery_date')}</label>
                  <input
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                    className="input-premium w-full"
                    data-testid="contract-delivery"
                  />
                </div>
              </div>

              {/* Additional Terms */}
              <div>
                <label className="overline block mb-2">{t('contracts_additional_terms_de')}</label>
                <textarea
                  value={formData.additional_terms}
                  onChange={(e) => setFormData({ ...formData, additional_terms: e.target.value })}
                  className="input-premium w-full h-24 resize-none"
                  placeholder={t('contracts_title') === 'Contracts' ? 'Special agreements, accessories, etc.' : 'Besondere Vereinbarungen, Zubehör, etc.'}
                  data-testid="contract-terms"
                />
              </div>
              <div>
                <label className="overline block mb-2">{t('contracts_additional_terms_en')}</label>
                <textarea
                  value={formData.additional_terms_en}
                  onChange={(e) => setFormData({ ...formData, additional_terms_en: e.target.value })}
                  className="input-premium w-full h-24 resize-none"
                  placeholder="Additional agreements (English version)"
                  data-testid="contract-terms-en"
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-[#E5E5E5]">
                {editingContract ? (
                  <>
                    <Button type="submit" className="btn-primary flex items-center gap-2">
                      <FileText size={18} />
                      {t('action_save')}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      {t('action_cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      type="button" 
                      onClick={(e) => handleSubmit(e, false)}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <FileText size={18} />
                      {t('contracts_save_draft')}
                    </Button>
                    <Button 
                      type="button" 
                      onClick={(e) => handleSubmit(e, true)}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Send size={18} />
                      {t('contracts_create_send')}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      {t('action_cancel')}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('contracts_search')}
            className="input-premium w-full pl-10"
            data-testid="contract-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="contract-status-filter">
            <Filter size={16} className="mr-2" />
            <SelectValue placeholder={t('contracts_filter_status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cars_filter_all')}</SelectItem>
            <SelectItem value="pending">{t('contracts_status_pending')}</SelectItem>
            <SelectItem value="sent">{t('contracts_status_sent')}</SelectItem>
            <SelectItem value="signed">{t('contracts_status_signed')}</SelectItem>
            <SelectItem value="cancelled">{t('contracts_status_cancelled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Contracts List */}
      {filteredContracts.length === 0 ? (
        <div className="text-center py-12 text-[#666666] bg-white rounded-lg border border-[#E5E5E5]">
          <FileText size={48} className="mx-auto mb-4 opacity-30" />
          <p>{t('contracts_no_results')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredContracts.map((contract) => (
            <div
              key={contract.id}
              className="bg-white border border-[#E5E5E5] rounded-lg p-4 md:p-6"
              data-testid={`contract-${contract.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Contract Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-heading text-lg">
                      {contract.car_brand} {contract.car_model}
                    </h3>
                    {getStatusBadge(contract.status)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-[#999999]">{t('contracts_title') === 'Contracts' ? 'Buyer:' : 'Käufer:'}</span>
                      <span className="ml-2 font-medium">{contract.buyer_name}</span>
                    </div>
                    <div>
                      <span className="text-[#999999]">{t('contracts_price')}:</span>
                      <span className="ml-2 font-medium text-gold">{formatPrice(contract.price)}</span>
                    </div>
                    <div>
                      <span className="text-[#999999]">{t('contracts_title') === 'Contracts' ? 'Created:' : 'Erstellt:'}</span>
                      <span className="ml-2">{formatDate(contract.created_at)}</span>
                    </div>
                    {contract.signed_at && (
                      <div>
                        <span className="text-[#999999]">{t('contracts_status_signed')}:</span>
                        <span className="ml-2 text-green-600">{formatDate(contract.signed_at)}</span>
                      </div>
                    )}
                  </div>
                  {/* Show signed document link if uploaded */}
                  {contract.signed_document_url && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <span className="text-green-600 flex items-center gap-1">
                        <FileText size={14} />
                        {t('contracts_title') === 'Contracts' ? 'Signed document:' : 'Unterschriebenes Dokument:'}
                      </span>
                      <a
                        href={`${API_URL}${contract.signed_document_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {contract.signed_document_filename || (t('contracts_title') === 'Contracts' ? 'View document' : 'Dokument ansehen')}
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {(contract.status === 'pending' || contract.status === 'draft') && (
                    <Button
                      onClick={() => handleSendContract(contract.id)}
                      disabled={sending === contract.id}
                      size="sm"
                      className="btn-secondary flex items-center gap-2"
                      data-testid={`send-contract-${contract.id}`}
                    >
                      {sending === contract.id ? (
                        <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                      ) : (
                        <Send size={16} />
                      )}
                      {t('contracts_send')}
                    </Button>
                  )}
                  <Link
                    to={`/contract/${contract.id}`}
                    target="_blank"
                    className="p-2 hover:bg-[#F9F9F7] rounded-lg transition-colors"
                    title={t('contracts_title') === 'Contracts' ? 'View contract' : 'Vertrag ansehen'}
                  >
                    <Eye size={18} />
                  </Link>
                  <button
                    onClick={() => handleEdit(contract)}
                    className="p-2 hover:bg-[#F9F9F7] rounded-lg transition-colors"
                    title={t('action_edit')}
                  >
                    <FileText size={18} />
                  </button>
                  {!contract.archived && (
                    <button
                      onClick={() => setArchiveId(contract.id)}
                      className="p-2 hover:bg-amber-50 text-amber-600 rounded-lg transition-colors"
                      title={t('contracts_archive')}
                    >
                      <FolderArchive size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(contract.id)}
                    className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                    title={t('action_delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={!!archiveId} onOpenChange={() => setArchiveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contracts_archive_confirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contracts_title') === 'Contracts' 
                ? 'This contract will be moved to the archive and will no longer appear in the main list. You can view archived contracts at any time via the Archive button.'
                : 'Dieser Vertrag wird ins Archiv verschoben und erscheint nicht mehr in der Hauptliste. Sie können archivierte Verträge jederzeit über den Archiv-Button einsehen.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('action_cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} className="bg-amber-600 hover:bg-amber-700">
              {t('contracts_archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-2">{t('contracts_settings')}</h3>
            <p className="text-sm text-[#666666] mb-6">
              {t('contracts_title') === 'Contracts'
                ? 'Configure settings for purchase contracts, including the dealer signature.'
                : 'Konfigurieren Sie die Einstellungen für Kaufverträge, einschließlich der Händler-Unterschrift.'}
            </p>
          </div>

          {/* Dealer Signature */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4">{t('contracts_dealer_signature')}</h3>
            <p className="text-sm text-[#666666] mb-4">
              {t('contracts_signature_hint')}
            </p>
            {settingsLoading ? (
              <div className="animate-pulse h-20 bg-gray-100 rounded-lg" />
            ) : (
              <div className="flex items-center gap-4">
                {settings?.dealer_signature_url ? (
                  <div className="relative">
                    <div className="border-2 border-dashed border-gray-300 p-4 rounded-lg bg-gray-50">
                      <img 
                        src={getImageUrl(settings.dealer_signature_url)} 
                        alt={t('contracts_dealer_signature')} 
                        className="h-20 object-contain"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSettingsChange('dealer_signature_url', null)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 p-8 rounded-lg text-center text-gray-400">
                    <FileSignature size={32} className="mx-auto mb-2" />
                    <span className="text-sm">{t('contracts_title') === 'Contracts' ? 'No signature' : 'Keine Unterschrift'}</span>
                  </div>
                )}
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Upload size={16} />
                  {t('contracts_title') === 'Contracts' ? 'Upload Signature' : 'Unterschrift hochladen'}
                </Button>
                {saving && (
                  <span className="text-sm text-[#666666]">{t('msg_loading')}</span>
                )}
              </div>
            )}
            <p className="text-xs text-[#999999] mt-3">
              {t('contracts_title') === 'Contracts' 
                ? 'Recommended: PNG format with transparent background, approx. 500x200 pixels'
                : 'Empfohlen: PNG-Format mit transparentem Hintergrund, ca. 500x200 Pixel'}
            </p>
          </div>

          {/* Contract Info */}
          <div className="bg-blue-50 p-6 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">{t('contracts_how_it_works')}</h4>
            <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
              {t('contracts_title') === 'Contracts' ? (
                <>
                  <li>Create a contract under <strong>Contracts</strong> or on the <strong>Customer detail page</strong></li>
                  <li>Click <strong>Send</strong> to email the contract to the customer</li>
                  <li>The customer can view the contract online and <strong>sign digitally</strong></li>
                  <li>After signing, you can download the <strong>PDF with both signatures</strong></li>
                </>
              ) : (
                <>
                  <li>Erstellen Sie einen Vertrag unter <strong>Verträge</strong> oder in der <strong>Kundendetailseite</strong></li>
                  <li>Klicken Sie auf <strong>Senden</strong>, um den Vertrag per E-Mail an den Kunden zu schicken</li>
                  <li>Der Kunde kann den Vertrag online ansehen und <strong>digital unterschreiben</strong></li>
                  <li>Nach der Unterschrift können Sie das <strong>PDF mit beiden Unterschriften</strong> herunterladen</li>
                </>
              )}
            </ol>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
